import {KVStore} from '../../../utils/KVStore';
import {startPickupWork} from '../graphql/mutations/startPickupWork';

/**
 * 「开始作业」打点的离线补偿。
 *
 * 打点失败时**不能只弹 toast**：司机在外面跑，弱网是常态，只提示会让实际出发时间静默丢失。
 * 但也不能塞进揽收结果那条离线队列（`PickedUpDataService`）——那条队列的条数在驱动菜单角标
 * 与任务列表刷新，混入打点会让司机看到"有揽收结果没传上去"的假提示。所以这里用**独立存储**，
 * 与揽收结果物理隔离，除本文件外无人读写。
 *
 * 四条约定：
 * 1. **时刻由调用方在点击那一刻定下**，不是补传那一刻。后端不收该值会取 `now()`，
 *    补传时就成了"恢复网络的时间"，实际出发时间失真
 * 2. **有待补记录就不再提示滑块**（见 `PickingUpPage` 的 needStartPage）。司机已经点过了，
 *    再弹一次等于让他在断网期间每次回到列表都被拦一遍
 * 3. 补传时机是**任务列表页挂载且查询成功之后**——查询成功本身就是"有网"的信号，不额外轮询
 * 4. 成功、后端明确拒绝、超过保留期，三种情况都删记录。只有网络错误才留着下次再试
 */

interface PendingWorkStart {
    taskId: string;
    /** 司机点击/滑动的时刻（ISO-8601 UTC） */
    startWorkTime: string;
    /** 落盘时刻，用于保留期判断 */
    savedAt: number;
}

export interface FlushWorkStartResult {
    /** 本次补传成功的任务，调用方据此把 pendingTaskId 清掉 */
    compensatedTaskIds: string[];
    /** 是否仍有待补记录（仍需抑制滑块提示） */
    remaining: boolean;
}

const store = new KVStore('pickup_work_start');
const PENDING_KEY = 'pending';

/**
 * 保留期 3 天。后端 `markWorkStarted` 的 CAS 只判 `act_departure_time IS NULL`，
 * **不判任务状态**——一条躺了很久的记录会写进早已完成或取消的任务里。
 * 同时它还在抑制滑块提示，留着不如丢掉。
 */
const RETENTION_MS = 3 * 24 * 60 * 60 * 1000;

const loadPending = async (): Promise<PendingWorkStart[]> => {
    const records = await store.getItem<PendingWorkStart[]>(PENDING_KEY);
    return Array.isArray(records) ? records : [];
};

const savePending = async (records: PendingWorkStart[]): Promise<void> => {
    if (records.length === 0) {
        await store.removeItem(PENDING_KEY);
        return;
    }
    await store.setItem(PENDING_KEY, records);
};

/** 打点失败时落盘，等下次有网补传 */
export const savePendingWorkStart = async (taskId: string, startWorkTime: string): Promise<void> => {
    if (!taskId) {
        return;
    }
    try {
        const records = await loadPending();
        // 同一任务重复点击只保留最早那次：实际出发时间是"第一次开工"，后面几次是重试
        if (records.some(record => record.taskId === taskId)) {
            return;
        }
        records.push({taskId, startWorkTime, savedAt: Date.now()});
        await savePending(records);
    } catch (error) {
        // 落盘都失败只能放弃：打点不是业务前置条件，绝不能因此阻断司机开工
        console.error('Failed to save pending work start:', error);
    }
};

/** 是否有待补记录。有 = 司机已经点过开始作业，不该再被滑块拦一次 */
export const hasPendingWorkStart = async (): Promise<boolean> => {
    try {
        return (await loadPending()).length > 0;
    } catch (error) {
        console.error('Failed to read pending work start:', error);
        return false;
    }
};

/** 尝试补传。调用方需在网络可用（如列表查询刚成功）时调用 */
export const flushPendingWorkStarts = async (): Promise<FlushWorkStartResult> => {
    const result: FlushWorkStartResult = {compensatedTaskIds: [], remaining: false};
    try {
        const records = await loadPending();
        if (records.length === 0) {
            return result;
        }
        const now = Date.now();
        const kept: PendingWorkStart[] = [];
        for (const record of records) {
            if (now - record.savedAt > RETENTION_MS) {
                console.warn('Drop expired pending work start:', record.taskId);
                continue;
            }
            try {
                await startPickupWork(record.taskId, record.startWorkTime);
                result.compensatedTaskIds.push(record.taskId);
            } catch (error: any) {
                if (error?.networkError) {
                    kept.push(record);
                } else {
                    // 后端明确拒绝（任务不存在 / 不是本人 / 参数非法），再试也不会成功。
                    // 留着会永久抑制滑块提示，等于把这个任务的打点入口彻底堵死
                    console.error('Drop rejected pending work start:', record.taskId, error);
                }
            }
        }
        await savePending(kept);
        result.remaining = kept.length > 0;
    } catch (error) {
        console.error('Failed to flush pending work start:', error);
    }
    return result;
};
